import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

export interface TrailerIdComboboxOption {
  id: string;
  trailer_id: string;
  scac?: string | null;
}

interface TrailerIdComboboxProps {
  value: string;
  onChange: (val: string) => void;
  trailers: TrailerIdComboboxOption[];
  placeholder?: string;
  emptyMessage?: string;
}

export function TrailerIdCombobox({
  value,
  onChange,
  trailers,
  placeholder = "Select or enter trailer ID...",
  emptyMessage = "No trailers available.",
}: TrailerIdComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = (search
    ? trailers.filter((t) => t.trailer_id.toLowerCase().includes(search.toLowerCase()))
    : trailers
  ).slice(0, 50);

  const hasExactMatch = trailers.some(
    (t) => t.trailer_id.toLowerCase() === search.toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value || <span className="text-muted-foreground">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Type or search trailer..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {search && !hasExactMatch && (
              <CommandItem
                value={search}
                onSelect={() => {
                  onChange(search.toUpperCase());
                  setSearch("");
                  setOpen(false);
                }}
              >
                Use "{search.toUpperCase()}"
              </CommandItem>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="Trailers">
                {filtered.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={t.trailer_id}
                    onSelect={() => {
                      onChange(t.trailer_id);
                      setSearch("");
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${value === t.trailer_id ? "opacity-100" : "opacity-0"}`}
                    />
                    <span className="flex-1">{t.trailer_id}</span>
                    {t.scac && (
                      <span className="text-xs text-muted-foreground ml-2">{t.scac}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filtered.length === 0 && !search && (
              <CommandEmpty>{emptyMessage}</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}